import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { attachPendingReferral } from "@/lib/antifraud/antifraud.functions";
import { getDeviceHash } from "@/lib/antifraud/deviceFingerprint";

export type RewardTier = "analista_pro" | "fan_oro" | "leyenda";

export const TIER_THRESHOLDS: Record<RewardTier, number> = {
  analista_pro: 3,
  fan_oro: 10,
  leyenda: 25,
};

export const TIER_LABELS: Record<RewardTier, string> = {
  analista_pro: "Analista Pro",
  fan_oro: "Fan Oro",
  leyenda: "Leyenda",
};

function rand(len = 8) {
  const a = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < len; i++) s += a[Math.floor(Math.random() * a.length)];
  return s;
}

export async function getOrCreateReferralCode(): Promise<string> {
  const sid = getSessionId();
  const stored = localStorage.getItem("ss2026_ref_code");
  if (stored) return stored;
  const code = `SS${rand(7)}`;
  await supabase.from("referrals").insert({
    referrer_session_id: sid,
    referral_code: code,
    status: "active",
  });
  localStorage.setItem("ss2026_ref_code", code);
  void trackEvent("share_link_generated", { code });
  return code;
}

export function buildReferralUrl(code: string): string {
  // SPRINT V3.1 — never use window.location.origin for outbound share URLs.
  // Always emit the canonical production host so links work outside the preview.
  return `https://versesport.com/?ref=${encodeURIComponent(code)}`;
}

export async function getReferralStats(): Promise<{
  count: number;
  unlocked: RewardTier[];
  next: { tier: RewardTier; remaining: number } | null;
}> {
  const sid = getSessionId();
  const { data: refs } = await supabase
    .from("referrals")
    .select("id,status")
    .eq("referrer_session_id", sid);
  const count = (refs ?? []).filter((r) => r.status === "completed").length;
  const unlocked = (Object.entries(TIER_THRESHOLDS) as [RewardTier, number][])
    .filter(([, n]) => count >= n)
    .map(([t]) => t);
  const next = (Object.entries(TIER_THRESHOLDS) as [RewardTier, number][])
    .find(([, n]) => count < n);
  return {
    count,
    unlocked,
    next: next ? { tier: next[0], remaining: next[1] - count } : null,
  };
}

export async function captureIncomingReferral() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("ref");
  if (!code) return;
  const sid = getSessionId();
  if (localStorage.getItem("ss2026_ref_used")) return;
  localStorage.setItem("ss2026_ref_used", code);
  // PR-34A — DO NOT auto-complete on mere page-visit. We attach the invited
  // session for telemetry, but the row stays "active" until the referred
  // user (a) verifies email and (b) makes a real first prediction.
  await supabase
    .from("referrals")
    .update({ invited_session_id: sid })
    .eq("referral_code", code);
  void trackEvent("referral_signup", { code, status: "pending" });

  // If the visitor is already authenticated, register a pending validation
  // server-side immediately so anti-self-referral and anti-multiaccount
  // checks run before any reward is granted.
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      const ua = navigator.userAgent ?? "";
      const uaHash = await sha256(ua);
      const deviceHash = (await getDeviceHash()) ?? undefined;
      await attachPendingReferral({ data: { code, uaHash, deviceHash } });
    }
  } catch {
    /* anti-fraud is best-effort on capture; finalization is the real gate */
  }
}

async function sha256(input: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

export async function unlockReward(tier: RewardTier) {
  const sid = getSessionId();
  await supabase.from("referral_rewards").insert({ session_id: sid, tier }).then(() => null);
  void trackEvent("reward_unlocked", { tier });
}

export function shareTargets(url: string, message: string) {
  const enc = encodeURIComponent;
  return {
    whatsapp: `https://wa.me/?text=${enc(`${message} ${url}`)}`,
    x: `https://twitter.com/intent/tweet?text=${enc(message)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    email: `mailto:?subject=${enc("VerseSport™")}&body=${enc(`${message}\n\n${url}`)}`,
  };
}
